import React, { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { FiSend, FiX } from 'react-icons/fi';
import { FestivalData } from '../../../../model/FestivalData';
import { useLanguage, useMessages } from '../../../../i18n/I18n';
import {
  AgentMessage, AgentPatch, AgentPatchError, AgentRequestError, AgentSection, applyAgentPatches, boundAgentHistory, invertAgentPatches, requestFestivalAgent,
} from '../../../../services/festivalAgentService';
import { describeAgentChanges } from './agentChanges';
import './AgentChat.css';

interface AgentChatProps {
  sessionToken: string;
  festivalId: string;
  festivalData: FestivalData;
  section: AgentSection;
  onApply: (nextData: FestivalData) => void;
  onClose: () => void;
  disabled?: boolean;
}

interface Proposal {
  snapshot: FestivalData;
  patches: AgentPatch[];
}

const AgentChat: React.FC<AgentChatProps> = ({ sessionToken, festivalId, festivalData, section, onApply, onClose, disabled = false }) => {
  const t = useMessages('agent');
  const { language, locale } = useLanguage();
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [undo, setUndo] = useState<Proposal | null>(null);
  const requestRef = useRef<AbortController | null>(null);
  const currentRef = useRef(festivalData);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  currentRef.current = festivalData;

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    inputRef.current?.focus();
    return () => {
      requestRef.current?.abort();
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) previouslyFocused.focus();
    };
  }, []);

  useEffect(() => {
    requestRef.current?.abort();
    requestRef.current = null;
    setBusy(false);
    setProposal(null);
    setUndo(null);
    setMessages([]);
    setError('');
    setNotice('');
  }, [festivalId, festivalData.version, section]);

  useEffect(() => {
    transcriptRef.current?.scrollTo?.({ top: transcriptRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  const review = useMemo(() => {
    if (!proposal) return null;
    try {
      applyAgentPatches(proposal.snapshot, festivalData, proposal.patches);
      return { changes: describeAgentChanges(proposal.snapshot, proposal.patches, t, locale), stale: false };
    } catch (cause) {
      if (cause instanceof AgentPatchError && cause.reason === 'stale') {
        return { changes: describeAgentChanges(proposal.snapshot, proposal.patches, t, locale), stale: true };
      }
      return { changes: [], stale: false, invalid: true };
    }
  }, [proposal, festivalData, t, locale]);

  const send = async (event: FormEvent) => {
    event.preventDefault();
    if (!input.trim() || busy || disabled) return;
    const conversation = boundAgentHistory([...messages, { role: 'user', content: input.trim() }]);
    const snapshot = currentRef.current;
    const controller = new AbortController();
    requestRef.current = controller;
    setMessages(conversation);
    setInput('');
    setBusy(true);
    setError('');
    setNotice('');
    setProposal(null);
    let timedOut = false;
    const timer = window.setTimeout(() => { timedOut = true; controller.abort(); }, 60000);
    try {
      const reply = await requestFestivalAgent(sessionToken, festivalId, snapshot, section, conversation, language, controller.signal);
      if (controller.signal.aborted) return;
      // Validate against the request snapshot before exposing any proposal to the organizer.
      applyAgentPatches(snapshot, snapshot, reply.patches);
      setMessages([...conversation, { role: 'assistant', content: reply.message }]);
      if (reply.patches.length) setProposal({ snapshot, patches: reply.patches });
    } catch (cause) {
      if (timedOut || !controller.signal.aborted) {
        setMessages(messages);
        setInput(input);
      }
      if (timedOut) setError(t.timeout);
      else if (!controller.signal.aborted) {
        setError(cause instanceof AgentPatchError ? t.invalidReply : cause instanceof AgentRequestError ? t.requestErrors[cause.reason] : t.requestFailed);
      }
    } finally {
      window.clearTimeout(timer);
      if (requestRef.current === controller) {
        requestRef.current = null;
        setBusy(false);
      }
    }
  };

  const apply = () => {
    if (!proposal || disabled || busy) return;
    try {
      const previous = currentRef.current;
      const next = applyAgentPatches(proposal.snapshot, previous, proposal.patches);
      const inverse = invertAgentPatches(previous, proposal.patches);
      onApply(next);
      setUndo({ snapshot: next, patches: inverse });
      setProposal(null);
      setNotice(t.applied);
    } catch (cause) {
      setError(cause instanceof AgentPatchError && cause.reason === 'stale' ? t.stale : t.invalidReply);
    }
  };

  const undoChanges = () => {
    if (!undo || disabled || busy) return;
    try {
      onApply(applyAgentPatches(undo.snapshot, currentRef.current, undo.patches));
      setUndo(null);
      setNotice(t.undone);
    } catch {
      setError(t.undoStale);
    }
  };

  return (
    <aside id="festival-agent-chat" className="agent-chat" aria-label={t.title} onKeyDown={event => { if (event.key === 'Escape') onClose(); }}>
      <header className="agent-chat-header">
        <h2>{t.title}</h2>
        <button type="button" className="btn btn-ghost btn-icon" aria-label={t.close} onClick={onClose}><FiX aria-hidden="true" /></button>
      </header>
      <div className="agent-chat-body">
        <p className="agent-chat-intro">{t.intro}</p>
        <div ref={transcriptRef} className="agent-chat-transcript" role="log" aria-live="polite" aria-relevant="additions text">
          {messages.map((message, index) => (
            <div key={index} className={`agent-chat-message agent-chat-message-${message.role}`}>
              <strong>{message.role === 'user' ? t.you : t.assistant}</strong>
              <p>{message.content}</p>
            </div>
          ))}
          {busy && <p className="agent-chat-busy" role="status">{t.busy}</p>}
        </div>
        {proposal && review && (
          <section className="agent-chat-proposal" aria-label={t.proposed}>
            <h3>{t.proposed}</h3>
            <ul>
              {review.changes.map((change, index) => (
                <li key={index}>
                  <strong>{change.label}</strong>
                  <div className="agent-chat-change"><span>{change.before}</span><span aria-hidden="true">→</span><span>{change.after}</span></div>
                </li>
              ))}
            </ul>
            {review.stale && <p role="alert" className="agent-chat-error">{t.stale}</p>}
            {review.invalid && <p role="alert" className="agent-chat-error">{t.invalidReply}</p>}
            <button type="button" className="btn btn-primary" onClick={apply} disabled={disabled || busy || review.stale || review.invalid}>{t.apply}</button>
          </section>
        )}
        {error && <p role="alert" className="agent-chat-error">{error}</p>}
        {notice && <p role="status" className="agent-chat-notice">{notice}</p>}
        {undo && <button type="button" className="btn agent-chat-undo" disabled={disabled || busy} onClick={undoChanges}>{t.undo}</button>}
      </div>
      <form className="agent-chat-composer" onSubmit={send}>
        <label htmlFor="festival-agent-message">{t.message}</label>
        <textarea id="festival-agent-message" ref={inputRef} value={input} onChange={event => setInput(event.target.value)} placeholder={t.placeholder} maxLength={2000} rows={3} disabled={busy || disabled} />
        <button type="submit" className="btn btn-primary" disabled={busy || disabled || !input.trim()}><FiSend aria-hidden="true" /> {t.send}</button>
        <p>{t.draftHint}</p>
      </form>
    </aside>
  );
};

export default AgentChat;
